// src/components/MemberCardTrigger.js
//
// The "member card" (PHOTO-002): hover (mouse, after a short pause) or tap
// (touch / click / Enter) a member's avatar → a small glass card with their
// photo, name and tagline.
//
// ZERO-REGRESSION SHAPE: when the member has no tagline this returns
// `children` untouched — same element, same layout, no handlers — so every
// avatar for a member without a tagline is exactly what it was.
//
// TRAP AVOIDED BY CONSTRUCTION: the card is portalled to <body> and positioned
// with viewport (fixed) coordinates. Avatars live inside glass cards
// (backdrop-filter), overflow:hidden rows, modals and sheets — any of which
// would trap or clip a fixed/absolute card rendered in place. That exact
// failure hit the photo framer on 2026-10-03.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getCachedMemberPhoto, getCachedMemberTagline, isRenderablePhotoUrl } from '../services/memberProfiles';

const CARD_WIDTH = 252;
const EDGE = 12;
const HOVER_OPEN_MS = 280;
const HOVER_CLOSE_MS = 160;

function initialsOf(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
}

function MemberCard({ anchor, name, tagline, photoUrl, cardRef, onPointerEnter, onPointerLeave }) {
    const [imgOk, setImgOk] = useState(true);
    const showPhoto = photoUrl && imgOk && isRenderablePhotoUrl(photoUrl);
    return (
        <div
            ref={cardRef}
            role="dialog"
            aria-label={`About ${name}`}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
            // Portalled DOM, but React events still bubble up the COMPONENT tree —
            // stop them so a tap on the card can never reach an ancestor's
            // click-to-close (e.g. a modal backdrop) wherever an avatar lives.
            onClick={(e) => e.stopPropagation()}
            style={{
                position: 'fixed', zIndex: 3100,
                left: anchor.left,
                top: anchor.top != null ? anchor.top : undefined,
                bottom: anchor.bottom != null ? anchor.bottom : undefined,
                width: CARD_WIDTH, maxWidth: `calc(100vw - ${EDGE * 2}px)`,
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px',
                background: 'rgba(255,255,255,0.88)',
                backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                border: '1px solid rgba(255,255,255,0.7)',
                borderRadius: 18,
                boxShadow: '0 2px 6px rgba(0,0,0,0.05), 0 14px 40px rgba(62,47,47,0.16), inset 0 1px 0 rgba(255,255,255,0.9)',
                animation: `${anchor.top != null ? 'momCardInDown' : 'momCardInUp'} 0.18s ease-out`,
                textAlign: 'left',
            }}
        >
            <div style={{
                width: 52, height: 52, borderRadius: '50%', flexShrink: 0, position: 'relative', overflow: 'hidden',
                background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px color-mix(in srgb, var(--mom-primary) 22%, transparent)',
            }}>
                <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 700, fontSize: 19, color: '#fff' }}>
                    {initialsOf(name)}
                </span>
                {showPhoto && (
                    <img src={photoUrl} alt="" draggable={false} onError={() => setImgOk(false)}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                    fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 17, lineHeight: 1.25,
                    color: 'var(--mom-charcoal)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{name}</div>
                <div style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, lineHeight: 1.45, marginTop: 3,
                    color: 'color-mix(in srgb, var(--mom-charcoal) 74%, var(--mom-cream))',
                    wordBreak: 'break-word',
                }}>{tagline}</div>
            </div>
        </div>
    );
}

// `display` lets callers keep the wrapper's box identical to what it wraps
// (e.g. 'inline-flex' for chips, 'block' for a row avatar).
function MemberCardTrigger({ userId, name, children, display = 'inline-flex', style }) {
    const tagline = getCachedMemberTagline(userId);
    const photoUrl = getCachedMemberPhoto(userId);
    const triggerRef = useRef(null);
    const cardRef = useRef(null);
    const openTimer = useRef(null);
    const closeTimer = useRef(null);
    const [open, setOpen] = useState(false);
    const [pinned, setPinned] = useState(false); // opened by tap/click/keyboard
    const [anchor, setAnchor] = useState(null);
    const [focused, setFocused] = useState(false);

    const place = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return null;
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth, vh = window.innerHeight;
        const width = Math.min(CARD_WIDTH, vw - EDGE * 2);
        const left = Math.max(EDGE, Math.min(r.left + r.width / 2 - width / 2, vw - EDGE - width));
        // Prefer below; flip above when there isn't room for the card.
        if (vh - r.bottom < 130 && r.top > vh - r.bottom) return { left, bottom: vh - r.top + 10 };
        return { left, top: r.bottom + 10 };
    }, []);

    const show = useCallback((pin) => {
        clearTimeout(openTimer.current); clearTimeout(closeTimer.current);
        const a = place();
        if (!a) return;
        setAnchor(a); setOpen(true);
        if (pin) setPinned(true);
    }, [place]);

    const hide = useCallback(() => {
        clearTimeout(openTimer.current); clearTimeout(closeTimer.current);
        setOpen(false); setPinned(false);
    }, []);

    useEffect(() => () => { clearTimeout(openTimer.current); clearTimeout(closeTimer.current); }, []);

    // Close on outside tap, Escape, scroll or resize (the card is fixed — it
    // must never drift away from its avatar).
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (triggerRef.current?.contains(e.target) || cardRef.current?.contains(e.target)) return;
            hide();
        };
        const onKey = (e) => { if (e.key === 'Escape') { hide(); triggerRef.current?.focus(); } };
        document.addEventListener('pointerdown', onDown, true);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        return () => {
            document.removeEventListener('pointerdown', onDown, true);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
        };
    }, [open, hide]);

    if (!tagline) return children;

    const onPointerEnter = (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(closeTimer.current);
        if (!open) openTimer.current = setTimeout(() => show(false), HOVER_OPEN_MS);
    };
    const onPointerLeave = (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(openTimer.current);
        if (!pinned) closeTimer.current = setTimeout(hide, HOVER_CLOSE_MS);
    };
    const onActivate = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (open && pinned) hide();
        else show(true); // a click on a hover-opened card pins it rather than closing it
    };

    return (
        <>
            <span
                ref={triggerRef}
                role="button"
                tabIndex={0}
                aria-label={`About ${name}`}
                aria-expanded={open}
                onClick={onActivate}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onActivate(e); }}
                onPointerEnter={onPointerEnter}
                onPointerLeave={onPointerLeave}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={{
                    display, cursor: 'pointer', borderRadius: 999, outline: 'none', flexShrink: 0,
                    boxShadow: focused ? '0 0 0 3px color-mix(in srgb, var(--mom-primary) 40%, transparent)' : 'none',
                    WebkitTapHighlightColor: 'transparent',
                    ...style,
                }}
            >{children}</span>
            {open && anchor && createPortal(
                <MemberCard
                    anchor={anchor}
                    name={name}
                    tagline={tagline}
                    photoUrl={photoUrl}
                    cardRef={cardRef}
                    onPointerEnter={(e) => { if (e.pointerType === 'mouse') clearTimeout(closeTimer.current); }}
                    onPointerLeave={(e) => { if (e.pointerType === 'mouse' && !pinned) closeTimer.current = setTimeout(hide, HOVER_CLOSE_MS); }}
                />,
                document.body
            )}
        </>
    );
}

export default MemberCardTrigger;
